import gsap from "gsap"
import Lenis from "lenis"
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'

gsap.registerPlugin(ScrollTrigger, CustomEase)

export default class Event {
    constructor({ scroll }) {
        this.scroll = scroll
        this.lightboxLenis = null
        this.init()
    }

    init() {
        this.initEventsSectionAnimation()
        this.initVideoEventHandlers()
    }

    initVideoEventHandlers() {
        // Wait a bit for Swiper to initialize
        setTimeout(() => {
            const videoTriggers = document.querySelectorAll('.event-video-trigger')
            
            videoTriggers.forEach((trigger) => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault()
                    
                    const videoUrl = trigger.dataset.videoUrl
                    const eventTitle = trigger.dataset.eventTitle
                    const eventDate = trigger.dataset.eventDate
                    
                    this.openVideoLightbox(videoUrl, eventTitle, eventDate)
                })
            })
        }, 100)
    }

    openVideoLightbox(videoUrl, title, date) {
        this.createLightboxElement()
        this.animateLightboxOpen()
        this.renderVideoContent(videoUrl, title, date)
        this.initLightboxScroll()
        this.attachCloseHandlers()
    }

    createLightboxElement() {
        const lightbox = document.createElement("div")
        lightbox.className = "event-video"
        lightbox.innerHTML = `
            <div class="event-video__overlay"></div>
            <div class="event-video__content">
                <div class="event-video__loader">
                    
                </div>
            </div>
        `
        document.body.appendChild(lightbox)
        
        document.body.style.overflow = "hidden"
        if (this.scroll) {
            this.scroll.stop()
        }
    }

    animateLightboxOpen() {
        gsap.fromTo(".event-video__overlay", 
            { 
                scaleY: 0,
                transformOrigin: "bottom"
            },
            { 
                scaleY: 1,
                duration: 0.8,
                ease: "power3.out"
            }
        )
        
        gsap.fromTo(".event-video__content",
            {
                y: "100%",
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: "power3.out",
                delay: 0.2
            }
        )
    }

    renderVideoContent(videoUrl, title, date) {
        const lightboxContent = document.querySelector(".event-video__content")
        lightboxContent.innerHTML = `
            <button class="event-video__close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" stroke-width="2"/>
                    <line x1="22" y1="2" x2="2" y2="22" stroke="currentColor" stroke-width="2"/>
                </svg>
            </button>
            <div class="event-video__inner">
                <div class="event-video__header">
                    <h3 class="title-secondary title-secondary--light">${title}</h3>
                    <span class="eyebrow eyebrow--light">${date}</span>
                </div>
                <div class="event-video__body">
                    <video src="${videoUrl}" controls autoplay controlsList="nodownload">
                        Your browser does not support the video tag.
                    </video>
                </div>
            </div>
        `
        
        gsap.from(".event-video__inner", {
            y: 30,
            opacity: 0,
            duration: 0.5,
            ease: "power2.out"
        })
    }

    initLightboxScroll() {
        const lightboxInner = document.querySelector(".event-video__inner")
        this.lightboxLenis = new Lenis({
            wrapper: lightboxInner,
            content: lightboxInner,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
        })
        
        const rafLightbox = (time) => {
            if (this.lightboxLenis) {
                this.lightboxLenis.raf(time)
                requestAnimationFrame(rafLightbox)
            }
        }
        requestAnimationFrame(rafLightbox)
    }

    attachCloseHandlers() {
        const closeButton = document.querySelector(".event-video__close")
        const overlay = document.querySelector(".event-video__overlay")
        
        if (closeButton) {
            closeButton.addEventListener("click", () => this.closeLightbox())
        }
        
        if (overlay) {
            overlay.addEventListener("click", () => this.closeLightbox())
        }
    }

    closeLightbox() {
        // Pause video before closing
        const video = document.querySelector(".event-video__player video")
        if (video) {
            video.pause()
        }
        
        if (this.lightboxLenis) {
            this.lightboxLenis.destroy()
            this.lightboxLenis = null
        }
        
        gsap.to(".event-video__overlay", {
            scaleY: 0,
            transformOrigin: "bottom",
            duration: 0.6,
            ease: "power3.in"
        })
        
        gsap.to(".event-video__content", {
            y: "100%",
            opacity: 0,
            duration: 0.6,
            ease: "power3.in",
            onComplete: () => {
                document.querySelector(".event-video")?.remove()
                document.body.style.overflow = ""
                if (this.scroll) {
                    this.scroll.start()
                }
            }
        })
    }

    initEventsSectionAnimation() {
        const eventsSection = document.querySelector('.events')
        
        if (!eventsSection) return
        
        const customEase = CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        
        const swiper = eventsSection.querySelector('.events__swiper')
        const slides = swiper ? swiper.querySelectorAll('.swiper-slide') : []
        const swiperMenu = swiper ? swiper.querySelector('.swiper-menu') : null
        
        if (slides.length > 0) {
            gsap.set(slides, { autoAlpha: 0, x: 150 })
        }
        
        if (swiperMenu) {
            gsap.set(swiperMenu, { autoAlpha: 0, x: 80 })
        }
        
        if (slides.length > 0) {
            slides.forEach((slide, index) => {
                gsap.fromTo(slide,
                    {
                        autoAlpha: 0,
                        x: 150
                    },
                    {
                        autoAlpha: 1,
                        x: 0,
                        duration: 0.9,
                        delay: index * 0.15,
                        ease: customEase,
                        scrollTrigger: {
                            trigger: '.events__container__content',
                            start: 'clamp(top 85%)',
                            once: true
                        }
                    }
                )
            })
        }
        
        if (swiperMenu) {
            gsap.fromTo(swiperMenu,
                {
                    autoAlpha: 0,
                    x: 80
                },
                {
                    autoAlpha: 1,
                    x: 0,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: customEase,
                    scrollTrigger: {
                        trigger: '.events__container__content',
                        start: 'clamp(top 85%)',
                        once: true
                    },
                    delay: 0.4
                }
            )
        }
    }
}
